import { useMutation, useQueryClient } from "@tanstack/react-query";

import { guacHttpClient } from "@/lib/http";
import {
  CalendarAndTasksData,
  CreateTaskRequest,
  DashboardTask,
  HttpResponse,
  Task,
} from "@/types";

// Placeholder ids are negative so they can never collide with a real task id.
let nextTempTaskId = -1;

const buildGeneralTask = (
  id: number,
  request: CreateTaskRequest,
): DashboardTask => ({
  id,
  type: null,
  title: request.title,
  dueDate: request.dueDate,
  completedDate: null,
  linkedEntityId: null,
  linkedStartDate: null,
  companyId: null,
  jobId: null,
  candidacyId: null,
  assigneeUserIds: request.assignees,
});

// Quick-adds a general (untyped) task from the dashboard task list. Inserted
// optimistically; the placeholder id is swapped for the real one on response.
export function useCreateAddDashboardTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (createRequest: CreateTaskRequest) => {
      const res = await guacHttpClient.post<
        Task,
        HttpResponse<Task>,
        CreateTaskRequest
      >("tasks", createRequest);
      return res.data;
    },
    onMutate: async (createRequest) => {
      await queryClient.cancelQueries({ queryKey: ["calendar-and-tasks"] });

      const tempId = nextTempTaskId--;
      queryClient.setQueriesData<CalendarAndTasksData>(
        { queryKey: ["calendar-and-tasks"] },
        (cachedData) =>
          cachedData
            ? {
                ...cachedData,
                tasks: [
                  ...cachedData.tasks,
                  buildGeneralTask(tempId, createRequest),
                ],
              }
            : cachedData,
      );

      return { tempId };
    },
    onSuccess: (created, _createRequest, context) => {
      queryClient.setQueriesData<CalendarAndTasksData>(
        { queryKey: ["calendar-and-tasks"] },
        (cachedData) =>
          cachedData
            ? {
                ...cachedData,
                tasks: cachedData.tasks.map((task) =>
                  task.id === context.tempId
                    ? { ...task, id: created.id }
                    : task,
                ),
              }
            : cachedData,
      );
    },
  });
}
